import React, { forwardRef } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, X, Maximize2, Minimize2 } from 'lucide-react';
import { cn } from '../../../../utils/cn';

export interface PanelProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 'default' | 'elevated' | 'bordered' | 'ghost';
  position?: 'left' | 'right' | 'top' | 'bottom' | 'center';
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  collapsible?: boolean;
  collapsed?: boolean;
  onCollapse?: (collapsed: boolean) => void;
  closable?: boolean;
  onClose?: () => void;
  resizable?: boolean;
  title?: string;
  subtitle?: string;
  actions?: React.ReactNode;
  className?: string;
  children: React.ReactNode;
}

const PANEL_VARIANTS = {
  default: 'bg-surface-primary border-neutral-200',
  elevated: 'bg-surface-elevated shadow-lg border-neutral-200/50',
  bordered: 'bg-surface-primary border-2 border-neutral-300',
  ghost: 'bg-surface-secondary/30 border-neutral-200/30'
};

const PANEL_SIZES = {
  sm: 'w-64',
  md: 'w-80',
  lg: 'w-96',
  xl: 'w-[32rem]',
  full: 'w-full'
};

const PANEL_POSITIONS = {
  left: 'border-r',
  right: 'border-l', 
  top: 'border-b',
  bottom: 'border-t',
  center: 'border'
};

export const Panel = forwardRef<HTMLDivElement, PanelProps>(({
  variant = 'default',
  position = 'center',
  size = 'md',
  collapsible = false,
  collapsed = false,
  onCollapse,
  closable = false,
  onClose,
  resizable = false,
  title,
  subtitle,
  actions,
  className,
  children,
  ...props
}, ref) => {
  const handleToggleCollapse = () => {
    onCollapse?.(!collapsed);
  };

  return (
    <motion.div
      ref={ref}
      className={cn(
        // Base styles
        'relative flex flex-col transition-all duration-300',
        
        // Variant styles
        PANEL_VARIANTS[variant],
        
        // Position styles
        PANEL_POSITIONS[position],
        
        // Size styles (when not collapsed)
        !collapsed && PANEL_SIZES[size],
        
        // Collapsed state
        collapsed && 'w-12',
        
        className
      )}
      initial={false}
      animate={{
        width: collapsed ? 48 : undefined,
        opacity: 1
      }}
             transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
       {...(props as any)}
    >
      {/* Panel Header */}
      {(title || collapsible || closable || actions) && (
        <div className={cn(
          'flex items-center justify-between p-4 border-b border-neutral-200',
          collapsed && 'px-2'
        )}>
          {/* Title Section */}
          <div className={cn('flex-1 min-w-0', collapsed && 'hidden')}>
            {title && (
              <h3 className="text-lg font-semibold text-neutral-900 truncate">
                {title}
              </h3>
            )}
            {subtitle && (
              <p className="text-sm text-neutral-600 truncate">
                {subtitle}
              </p>
            )}
          </div>
          
          {/* Actions Section */}
          <div className="flex items-center space-x-1">
            {/* Custom Actions */}
            {!collapsed && actions && (
              <div className="flex items-center space-x-1">
                {actions}
              </div>
            )}
            
            {/* Collapse Toggle */}
            {collapsible && (
              <button
                onClick={handleToggleCollapse}
                className="p-1.5 rounded-lg hover:bg-neutral-100 transition-colors"
                aria-label={collapsed ? 'Expand panel' : 'Collapse panel'}
              >
                {position === 'left' ? (
                  collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />
                ) : position === 'right' ? (
                  collapsed ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />
                ) : (
                  collapsed ? <Maximize2 className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />
                )}
              </button>
            )}
            
            {/* Close Button */}
            {closable && (
              <button
                onClick={onClose}
                className={cn(
                  'p-1.5 rounded-lg hover:bg-red-100 hover:text-red-600 transition-colors',
                  collapsed && 'hidden'
                )}
                aria-label="Close panel"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      )}
      
      {/* Panel Content */}
      <div className={cn(
        'flex-1 overflow-hidden',
        collapsed && 'hidden'
      )}>
        {children}
      </div>
      
      {/* Collapsed State Indicator */}
      {collapsed && title && (
        <div className="p-2 text-center">
          <div className="w-8 h-8 mx-auto bg-primary-100 rounded-lg flex items-center justify-center">
            <span className="text-primary-600 text-xs font-bold">
              {title.charAt(0).toUpperCase()}
            </span>
          </div>
        </div>
      )}
      
      {/* Resize Handle */}
      {resizable && !collapsed && (
        <div className={cn(
          'absolute top-0 bottom-0 w-1 cursor-col-resize hover:bg-primary-500/20 transition-colors',
          position === 'left' && 'right-0',
          position === 'right' && 'left-0'
        )} />
      )}
    </motion.div>
  );
});

Panel.displayName = 'Panel';

// Panel Group Component for managing multiple panels
export interface PanelGroupProps {
  orientation?: 'horizontal' | 'vertical';
  className?: string;
  children: React.ReactNode;
}

export const PanelGroup: React.FC<PanelGroupProps> = ({
  orientation = 'horizontal',
  className,
  children
}) => {
  return (
    <div className={cn(
      'flex h-full',
      orientation === 'horizontal' ? 'flex-row' : 'flex-col',
      className
    )}>
      {children}
    </div>
  );
};

// Floating Panel Component for overlays and modals
export interface FloatingPanelProps extends Omit<PanelProps, 'position'> {
  visible?: boolean;
  onVisibilityChange?: (visible: boolean) => void;
  placement?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'center';
  backdrop?: boolean;
  backdropBlur?: boolean;
}

export const FloatingPanel: React.FC<FloatingPanelProps> = ({
  visible = false,
  onVisibilityChange,
  placement = 'center',
  backdrop = true,
  backdropBlur = true,
  className,
  children,
  ...panelProps
}) => {
  const getPlacementStyles = () => {
    switch (placement) {
      case 'top-left':
        return 'top-4 left-4';
      case 'top-right':
        return 'top-4 right-4';
      case 'bottom-left':
        return 'bottom-4 left-4';
      case 'bottom-right':
        return 'bottom-4 right-4';
      case 'center':
      default:
        return 'top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2';
    }
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-50">
      {/* Backdrop */}
      {backdrop && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className={cn(
            'absolute inset-0 bg-black/50',
            backdropBlur && 'backdrop-blur-sm'
          )}
          onClick={() => onVisibilityChange?.(false)}
        />
      )}
      
      {/* Floating Panel */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className={cn(
          'absolute',
          getPlacementStyles(),
          className
        )}
      >
        <Panel
          variant="elevated"
          closable
          onClose={() => onVisibilityChange?.(false)}
          className="shadow-2xl border-0"
          {...panelProps}
        >
          {children}
        </Panel>
      </motion.div>
    </div>
  );
};

// Scrollable Panel Content
export interface ScrollablePanelContentProps extends React.HTMLAttributes<HTMLDivElement> {
  maxHeight?: string;
  showScrollbar?: boolean;
  className?: string;
  children: React.ReactNode;
}

export const ScrollablePanelContent: React.FC<ScrollablePanelContentProps> = ({
  maxHeight = '100%',
  showScrollbar = true,
  className,
  children,
  ...props
}) => {
  return (
    <div
      className={cn(
        'overflow-y-auto',
        !showScrollbar && 'scrollbar-hide',
        className
      )}
      style={{ maxHeight }}
      {...props}
    >
      {children}
    </div>
  );
}; 